import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Search, FileText, CheckSquare, Users, UserSearch, Network, Filter, X, UsersRound,
  SlidersHorizontal, Brain, Lightbulb, MessageSquare, Calendar, Video, BookOpen,
  GraduationCap, Clock, Shield, ArrowRight, Loader2, Sparkles, ChevronRight,
  RefreshCw, HelpCircle, Tag, BarChart3
} from 'lucide-react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_META = {
  articles: { icon: FileText, color: '#002FA7', label: 'Article', link: '/articles' },
  tasks: { icon: CheckSquare, color: '#34C759', label: 'Task', link: '/tasks' },
  users: { icon: Users, color: '#FF9500', label: 'User', link: '/users' },
  expertise: { icon: UserSearch, color: '#AF52DE', label: 'Expertise', link: '/expertise' },
  knowledge_maps: { icon: Network, color: '#00C7BE', label: 'Knowledge Map', link: '/knowledge-maps' },
  teams: { icon: UsersRound, color: '#002FA7', label: 'Team', link: '/teams' },
  captures: { icon: Lightbulb, color: '#FF9500', label: 'Knowledge Capture', link: '/workspace' },
  lessons: { icon: GraduationCap, color: '#5856D6', label: 'Lesson Learned', link: '/workspace' },
  pkm_notes: { icon: Brain, color: '#002FA7', label: 'PKM Note', link: '/pkm' },
  webinars: { icon: Video, color: '#5856D6', label: 'Webinar', link: '/webinars' },
};

const TYPE_BADGE = {
  official: { label: 'Official Policy', color: '#FF3B30' },
  published: { label: 'Published', color: '#34C759' },
  informal: { label: 'Informal', color: '#FF9500' },
  task: { label: 'Task', color: '#007AFF' },
  lesson: { label: 'Lesson', color: '#5856D6' },
  note: { label: 'Note', color: '#002FA7' },
  webinar: { label: 'Webinar', color: '#AF52DE' },
  document: { label: 'Document', color: '#4B5563' },
};

const SearchPage = () => {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState(null);
  const [aiInterpretation, setAiInterpretation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [teams, setTeams] = useState([]);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterTeam, setFilterTeam] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchTeams(); const q = searchParams.get('q'); if (q) { setQuery(q); performSearch(q); } }, []);
  useEffect(() => { const q = searchParams.get('q'); if (q && q !== query) { setQuery(q); performSearch(q); } }, [searchParams]);

  const fetchTeams = async () => { try { const res = await axios.get(`${API}/teams`, { headers }); setTeams(res.data); } catch {} };

  const performSearch = async (q) => {
    if (!q?.trim()) return;
    setLoading(true);
    setAiInterpretation(null);
    try {
      const params = { q };
      if (filterType && filterType !== 'all_types') params.type = filterType;
      if (filterStatus && filterStatus !== 'any_status') params.status = filterStatus;
      if (filterPriority && filterPriority !== 'any_priority') params.priority = filterPriority;
      if (filterTeam && filterTeam !== 'any_team') params.team_id = filterTeam;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      const res = await axios.get(`${API}/search`, { params, headers });
      setResults(res.data);
      const total = Object.values(res.data).reduce((s, arr) => s + (arr?.length || 0), 0);
      axios.post(`${API}/search/log`, { query: q, total_results: total, used_ai: false }, { headers }).catch(() => {});
    } catch { toast.error('Search failed'); }
    finally { setLoading(false); }
  };

  const performAiSearch = async () => {
    if (!query?.trim()) return;
    setAiLoading(true);
    try {
      const res = await axios.post(`${API}/search/ai`, { query }, { headers });
      setAiInterpretation(res.data.ai_interpretation);
      setResults(res.data.results);
      axios.post(`${API}/search/log`, { query, total_results: res.data.total_found || 0, used_ai: true }, { headers }).catch(() => {});
    } catch { toast.error('AI search failed'); }
    finally { setAiLoading(false); }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) setSearchParams({ q: query });
    performSearch(query);
  };

  const handleSuggestionClick = (term) => { setQuery(term); setSearchParams({ q: term }); performSearch(term); };

  const logClick = (item, source) => {
    const id = item.article_id || item.task_id || item.capture_id || item.lesson_id || item.note_id || item.webinar_id || '';
    const title = item.title || item.name || item.content?.substring(0, 60) || '';
    axios.post(`${API}/search/click`, { query, result_id: id, result_type: source, result_title: title }, { headers }).catch(() => {});
  };

  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try { const res = await axios.get(`${API}/search/analytics`, { headers }); setAnalytics(res.data); }
    catch { toast.error('Failed to load analytics'); }
    finally { setAnalyticsLoading(false); }
  };

  const clearFilters = () => { setFilterType(''); setFilterStatus(''); setFilterPriority(''); setFilterTeam(''); setFilterDateFrom(''); setFilterDateTo(''); };
  const hasActiveFilters = filterType || filterStatus || filterPriority || filterTeam || filterDateFrom || filterDateTo;

  const totalResults = results ? Object.values(results).reduce((sum, arr) => sum + (arr?.length || 0), 0) : 0;

  // Flatten all results for "All" tab, sorted by relevance
  const allResults = results ? Object.entries(results).flatMap(([source, items]) =>
    (items || []).map(item => ({ ...item, _source: item._source || source }))
  ).sort((a, b) => (b._relevance || 0) - (a._relevance || 0)) : [];

  const renderResult = (item, source) => {
    const meta = SOURCE_META[source] || SOURCE_META.articles;
    const Icon = meta.icon;
    const typeBadge = TYPE_BADGE[item._content_type] || TYPE_BADGE.document;
    const title = item.title || item.name || item.content?.substring(0, 60) || 'Untitled';
    const snippet = item._snippet || item.content?.substring(0, 160) || item.description?.substring(0, 160) || '';
    const date = item.updated_at || item.created_at || '';
    const author = item.created_by_name || item.user_name || item.name || '';
    const relevance = item._relevance || 50;
    const relColor = relevance >= 80 ? '#34C759' : relevance >= 60 ? '#FF9500' : '#9CA3AF';

    return (
      <Link to={meta.link} key={item.article_id || item.task_id || item.user_id || item.capture_id || item.lesson_id || item.note_id || item.webinar_id || item.map_id || item.team_id || Math.random()} onClick={() => logClick(item, source)}>
        <Card className="border-[#E5E7EB] hover:border-[#002FA7]/30 hover:shadow-sm transition-all mb-2" data-testid={`search-result-${source}`}>
          <CardContent className="p-3">
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: meta.color + '12' }}>
                <Icon className="w-4 h-4" style={{ color: meta.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  <h4 className="text-sm font-medium text-[#0A0A0A] truncate">{title}</h4>
                  <Badge className="text-[8px] px-1.5 py-0" style={{ backgroundColor: meta.color + '12', color: meta.color }}>{meta.label}</Badge>
                  <Badge className="text-[8px] px-1.5 py-0" style={{ backgroundColor: typeBadge.color + '12', color: typeBadge.color }}>{typeBadge.label}</Badge>
                  {item.priority && <Badge className="text-[8px] px-1 py-0 bg-[#F3F4F6] text-[#4B5563]">{item.priority}</Badge>}
                  {item.status && <Badge className="text-[8px] px-1 py-0 bg-[#F3F4F6] text-[#4B5563]">{item.status}</Badge>}
                </div>
                <p className="text-xs text-[#4B5563] line-clamp-2 leading-relaxed">{snippet}</p>
                <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                  {date && <span className="text-[9px] text-[#9CA3AF] flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />{date.substring(0, 10)}</span>}
                  {author && <span className="text-[9px] text-[#9CA3AF] flex items-center gap-0.5"><Users className="w-2.5 h-2.5" />{author}</span>}
                  <span className="text-[9px] font-medium flex items-center gap-0.5" style={{ color: relColor }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: relColor }} />{relevance}% match
                  </span>
                  {item.tags?.length > 0 && item.tags.slice(0, 3).map((tag, i) => (
                    <Badge key={i} variant="outline" className="text-[7px] px-1 border-[#E5E7EB]">{tag}</Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </Link>
    );
  };

  return (
    <div data-testid="search-page">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-[#0A0A0A]">Intelligent Search</h1>
            <p className="text-[#4B5563] text-sm">AI-powered search across all knowledge — articles, tasks, lessons, captures, webinars, and more</p>
          </div>
          <Button variant="outline" className="text-xs" onClick={fetchAnalytics} data-testid="analytics-btn">
            <BarChart3 className="w-3.5 h-3.5 mr-1" />Search Analytics
          </Button>
        </div>
      </div>

      {/* Search Bar */}
      <Card className="border-[#E5E7EB] mb-4">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
              <Input type="search" placeholder="Ask anything... 'How do I reset my password?' or 'Q3 budget report'" value={query} onChange={(e) => setQuery(e.target.value)} className="pl-12 h-12 text-base" data-testid="global-search-input" />
            </div>
            <Button type="submit" className="bg-[#002FA7] hover:bg-[#00227A] h-12 px-5" data-testid="search-btn">
              <Search className="w-4 h-4 mr-1.5" />Search
            </Button>
            <Button type="button" className="h-12 bg-[#5856D6] hover:bg-[#4845B0]" onClick={performAiSearch} disabled={aiLoading || !query.trim()} data-testid="ai-search-btn">
              {aiLoading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Brain className="w-4 h-4 mr-1.5" />}
              AI Search
            </Button>
            <Button type="button" variant={showFilters ? 'default' : 'outline'} className={`h-12 ${showFilters ? 'bg-[#002FA7]' : ''}`} onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters-btn">
              <SlidersHorizontal className="w-4 h-4 mr-1.5" />Filters
              {hasActiveFilters && <span className="ml-1 w-2 h-2 bg-[#FF3B30] rounded-full" />}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Advanced Filters */}
      {showFilters && (
        <Card className="border-[#E5E7EB] mb-4" data-testid="search-filters">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold flex items-center gap-2"><Filter className="w-4 h-4" />Advanced Filters</h3>
              {hasActiveFilters && <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs text-[#FF3B30]" data-testid="clear-filters-btn"><X className="w-3 h-3 mr-1" />Clear all</Button>}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <div><Label className="text-xs">Type</Label><Select value={filterType || "all_types"} onValueChange={v => setFilterType(v === "all_types" ? "" : v)}><SelectTrigger className="h-9" data-testid="filter-type"><SelectValue placeholder="All" /></SelectTrigger><SelectContent><SelectItem value="all_types">All types</SelectItem><SelectItem value="articles">Articles</SelectItem><SelectItem value="tasks">Tasks</SelectItem><SelectItem value="captures">Captures</SelectItem><SelectItem value="lessons">Lessons</SelectItem><SelectItem value="pkm_notes">PKM Notes</SelectItem><SelectItem value="webinars">Webinars</SelectItem><SelectItem value="users">Users</SelectItem><SelectItem value="teams">Teams</SelectItem></SelectContent></Select></div>
              <div><Label className="text-xs">Status</Label><Select value={filterStatus || "any_status"} onValueChange={v => setFilterStatus(v === "any_status" ? "" : v)}><SelectTrigger className="h-9" data-testid="filter-status"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="any_status">Any status</SelectItem><SelectItem value="todo">To Do</SelectItem><SelectItem value="in_progress">In Progress</SelectItem><SelectItem value="review">Review</SelectItem><SelectItem value="blocked">Blocked</SelectItem><SelectItem value="done">Done</SelectItem></SelectContent></Select></div>
              <div><Label className="text-xs">Priority</Label><Select value={filterPriority || "any_priority"} onValueChange={v => setFilterPriority(v === "any_priority" ? "" : v)}><SelectTrigger className="h-9" data-testid="filter-priority"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="any_priority">Any priority</SelectItem><SelectItem value="critical">Critical</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="low">Low</SelectItem></SelectContent></Select></div>
              <div><Label className="text-xs">Team</Label><Select value={filterTeam || "any_team"} onValueChange={v => setFilterTeam(v === "any_team" ? "" : v)}><SelectTrigger className="h-9" data-testid="filter-team"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="any_team">Any team</SelectItem>{teams.map(t => <SelectItem key={t.team_id} value={t.team_id}>{t.name}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs">From</Label><Input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} className="h-9" data-testid="filter-date-from" /></div>
              <div><Label className="text-xs">To</Label><Input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} className="h-9" data-testid="filter-date-to" /></div>
            </div>
            <Button size="sm" className="mt-3 bg-[#002FA7] hover:bg-[#00227A]" onClick={() => performSearch(query)} data-testid="apply-filters-btn">Apply Filters</Button>
          </CardContent>
        </Card>
      )}

      {/* AI Interpretation */}
      {aiInterpretation && (
        <Card className="border-[#5856D6]/20 bg-[#5856D6]/[0.02] mb-4" data-testid="ai-interpretation">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#5856D6]/10 flex items-center justify-center flex-shrink-0">
                <Brain className="w-4 h-4 text-[#5856D6]" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-xs font-semibold text-[#5856D6]">AI Search Intelligence</h3>
                  {aiInterpretation.is_ambiguous && <Badge className="text-[8px] bg-[#FF9500]/10 text-[#FF9500]">Ambiguous Query</Badge>}
                </div>
                <p className="text-xs text-[#0A0A0A] font-medium mb-1" data-testid="ai-intent">{aiInterpretation.interpreted_intent}</p>
                <p className="text-[11px] text-[#4B5563] mb-2" data-testid="ai-summary">{aiInterpretation.result_summary}</p>

                {aiInterpretation.top_recommendation && (
                  <div className="p-2 rounded-lg bg-[#34C759]/5 border border-[#34C759]/20 mb-2">
                    <p className="text-[10px] text-[#34C759] flex items-center gap-1"><Sparkles className="w-3 h-3" /><strong>Top Recommendation:</strong> {aiInterpretation.top_recommendation}</p>
                  </div>
                )}

                {aiInterpretation.alternative_interpretations?.length > 0 && (
                  <div className="mb-2">
                    <p className="text-[9px] text-[#9CA3AF] mb-1">Did you mean:</p>
                    <div className="flex gap-1 flex-wrap">
                      {aiInterpretation.alternative_interpretations.map((alt, i) => (
                        <button key={i} onClick={() => handleSuggestionClick(alt)} className="text-[10px] text-[#5856D6] px-2 py-0.5 rounded-full border border-[#5856D6]/20 hover:bg-[#5856D6]/10 transition-colors" data-testid={`alt-interpretation-${i}`}>
                          {alt}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {aiInterpretation.related_terms?.length > 0 && (
                  <div className="flex items-center gap-1 flex-wrap">
                    <Tag className="w-2.5 h-2.5 text-[#9CA3AF]" />
                    {aiInterpretation.related_terms.map((term, i) => (
                      <button key={i} onClick={() => handleSuggestionClick(term)} className="text-[9px] text-[#002FA7] hover:underline" data-testid={`related-term-${i}`}>{term}</button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" /></div>
      ) : results && (
        <>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-[#4B5563]">
              Found <strong>{totalResults}</strong> result{totalResults !== 1 ? 's' : ''}{query ? ` for "${query}"` : ''}
            </p>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-3 bg-white border border-[#E5E7EB] flex-wrap">
              <TabsTrigger value="all" data-testid="tab-all">All ({totalResults})</TabsTrigger>
              {Object.entries(SOURCE_META).map(([key, meta]) => {
                const count = results[key]?.length || 0;
                if (count === 0) return null;
                const Icon = meta.icon;
                return <TabsTrigger key={key} value={key} data-testid={`tab-${key}`}><Icon className="w-3 h-3 mr-1" />{meta.label} ({count})</TabsTrigger>;
              })}
            </TabsList>

            <TabsContent value="all">
              {allResults.length > 0 ? (
                <div data-testid="all-results">
                  {allResults.map((item, i) => renderResult(item, item._source))}
                </div>
              ) : (
                <Card className="border-[#E5E7EB]" data-testid="no-results">
                  <CardContent className="p-12 text-center">
                    <Search className="w-10 h-10 mx-auto mb-3 text-[#E5E7EB]" />
                    <p className="text-sm text-[#4B5563] mb-2">No results found for "{query}"</p>
                    <p className="text-xs text-[#9CA3AF] mb-4">Try broader search terms, check spelling, or use the AI Search for natural language queries.</p>
                    <div className="flex justify-center gap-2">
                      <Button variant="outline" size="sm" className="text-xs" onClick={performAiSearch} data-testid="try-ai-search">
                        <Brain className="w-3 h-3 mr-1" />Try AI Search
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {Object.entries(SOURCE_META).map(([key]) => (
              <TabsContent key={key} value={key}>
                {(results[key]?.length || 0) > 0 ? (
                  <div>{results[key].map((item) => renderResult(item, key))}</div>
                ) : <p className="text-[#9CA3AF] text-center py-4">No {key.replace('_', ' ')} found</p>}
              </TabsContent>
            ))}
          </Tabs>

          {/* Follow-up Prompt */}
          {totalResults > 0 && (
            <Card className="border-[#E5E7EB] mt-4 bg-[#F9FAFB]" data-testid="follow-up-prompt">
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <HelpCircle className="w-4 h-4 text-[#9CA3AF] flex-shrink-0" />
                  <p className="text-xs text-[#4B5563]">
                    Didn't find what you need? Try <button onClick={performAiSearch} className="text-[#5856D6] font-medium hover:underline">AI Search</button> for natural language,
                    <button onClick={() => setShowFilters(true)} className="text-[#002FA7] font-medium hover:underline ml-1">refine by filters</button>, or
                    {aiInterpretation?.follow_up_suggestions?.map((s, i) => (
                      <span key={i} className="text-[#4B5563]"> {s.toLowerCase()}{i < (aiInterpretation.follow_up_suggestions.length - 1) ? ',' : '.'}</span>
                    ))}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Search Analytics Panel */}
      {analytics && (
        <Card className="border-[#E5E7EB] mb-4" data-testid="search-analytics-panel">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#002FA7]" />Search Analytics (30 days)</CardTitle>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setAnalytics(null)}><X className="w-3 h-3" /></Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
              {[
                { label: 'Total Searches', value: analytics.summary.total_searches, color: '#002FA7' },
                { label: 'AI Searches', value: analytics.summary.ai_searches, color: '#5856D6' },
                { label: 'Clicks', value: analytics.summary.total_clicks, color: '#34C759' },
                { label: 'Click-Through', value: `${analytics.summary.click_through_rate}%`, color: analytics.summary.click_through_rate >= 50 ? '#34C759' : '#FF9500' },
                { label: 'Unique Searchers', value: analytics.summary.unique_searchers, color: '#007AFF' },
                { label: 'Zero Results', value: `${analytics.summary.zero_result_rate}%`, color: analytics.summary.zero_result_rate <= 10 ? '#34C759' : '#FF3B30' },
              ].map((s, i) => (
                <div key={i} className="p-2 rounded-lg bg-[#F9FAFB] text-center" data-testid={`analytics-stat-${i}`}>
                  <p className="text-lg font-bold" style={{ color: s.color }}>{s.value}</p>
                  <p className="text-[9px] text-[#9CA3AF]">{s.label}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Top Queries */}
              <div data-testid="top-queries">
                <h4 className="text-xs font-semibold mb-2 flex items-center gap-1"><Search className="w-3 h-3 text-[#002FA7]" />Popular Queries</h4>
                {analytics.top_queries.length === 0 ? <p className="text-[10px] text-[#9CA3AF]">No data yet</p> :
                  analytics.top_queries.slice(0, 8).map((q, i) => (
                    <div key={i} className="flex items-center justify-between py-1 border-b border-[#F3F4F6] last:border-0">
                      <button onClick={() => handleSuggestionClick(q.query)} className="text-[11px] text-[#002FA7] hover:underline truncate">{q.query}</button>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-[9px] text-[#9CA3AF]">{q.avg_results} avg results</span>
                        <Badge variant="secondary" className="text-[9px]">{q.count}x</Badge>
                      </div>
                    </div>
                  ))
                }
              </div>

              {/* Zero-Result Queries / Knowledge Gaps */}
              <div data-testid="knowledge-gaps">
                <h4 className="text-xs font-semibold mb-2 flex items-center gap-1 text-[#FF3B30]"><HelpCircle className="w-3 h-3" />Knowledge Gaps (Zero Results)</h4>
                {analytics.knowledge_gaps.length === 0 && analytics.zero_result_queries.length === 0 ? <p className="text-[10px] text-[#34C759]">No gaps detected</p> :
                  (analytics.knowledge_gaps.length > 0 ? analytics.knowledge_gaps : analytics.zero_result_queries).slice(0, 8).map((q, i) => (
                    <div key={i} className="flex items-center justify-between py-1 border-b border-[#F3F4F6] last:border-0" data-testid={`gap-${i}`}>
                      <span className="text-[11px] text-[#FF3B30] truncate">{q.query}</span>
                      <Badge className="text-[9px] bg-[#FF3B30]/10 text-[#FF3B30]">{q.searches || q.count}x searched</Badge>
                    </div>
                  ))
                }
              </div>

              {/* Most Clicked Results */}
              <div data-testid="top-clicked">
                <h4 className="text-xs font-semibold mb-2 flex items-center gap-1"><Sparkles className="w-3 h-3 text-[#34C759]" />Most Clicked</h4>
                {analytics.top_clicked_results.length === 0 ? <p className="text-[10px] text-[#9CA3AF]">No clicks yet</p> :
                  analytics.top_clicked_results.slice(0, 8).map((c, i) => (
                    <div key={i} className="flex items-center justify-between py-1 border-b border-[#F3F4F6] last:border-0">
                      <div className="flex items-center gap-1 min-w-0">
                        <Badge className="text-[7px] px-1 flex-shrink-0" style={{ backgroundColor: (SOURCE_META[c.type]?.color || '#999') + '12', color: SOURCE_META[c.type]?.color || '#999' }}>{c.type}</Badge>
                        <span className="text-[11px] text-[#0A0A0A] truncate">{c.title}</span>
                      </div>
                      <Badge variant="secondary" className="text-[9px] flex-shrink-0">{c.clicks} clicks</Badge>
                    </div>
                  ))
                }
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!results && !loading && (
        <Card className="border-[#E5E7EB]">
          <CardContent className="p-12 text-center">
            <Search className="w-12 h-12 mx-auto mb-4 text-[#E5E7EB]" />
            <h3 className="text-lg font-medium text-[#0A0A0A] mb-2">Search KStreak</h3>
            <p className="text-sm text-[#4B5563] mb-4 max-w-md mx-auto">Search across articles, tasks, lessons learned, workspace captures, PKM notes, webinars, users, and more. Try natural language like "How do I reset my password?"</p>
            <div className="flex flex-wrap justify-center gap-2">
              {['Q3 report', 'deployment process', 'onboarding guide', 'API documentation', 'security policy'].map((term, i) => (
                <button key={i} onClick={() => handleSuggestionClick(term)} className="text-xs text-[#002FA7] px-3 py-1.5 rounded-full border border-[#002FA7]/20 hover:bg-[#002FA7]/5 transition-colors" data-testid={`suggestion-${i}`}>
                  {term}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SearchPage;
